import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { getActions } from '@/features/patient-actions/apis/patientActionsApi'
import { Action } from '@/features/patient-actions/types/patientActions'

// ========================================
// Constants
// ========================================

const POLLING_INTERVAL = 2000 // 2秒固定間隔
const MAX_POLLS = 5 // 最大5回（10秒で完了）

// ========================================
// useHomeCareActionQuery
// ========================================

interface UseHomeCareActionQueryReturn {
  homeCareAction: Action | null
  isLoading: boolean
}

/**
 * 在宅介入判定アクション取得クエリ（TanStack Query + ポーリング）
 *
 * 責務:
 * - SOAP生成後の在宅介入判定結果取得
 * - 固定間隔ポーリング（2秒、最大5回）
 *
 * 使用例:
 * ```tsx
 * const { homeCareAction, isLoading } = useHomeCareActionQuery('request-event-id')
 * ```
 */
export const useHomeCareActionQuery = (
  yakurekiId: string | undefined
): UseHomeCareActionQueryReturn => {
  const query = useQuery({
    queryKey: [...queryKeys.patientActions.list(), 'homeCare', yakurekiId],
    queryFn: async (): Promise<Action | null> => {
      if (!yakurekiId) return null

      try {
        const response = await getActions({
          yakureki: yakurekiId,
          group: 'home_care',
          status: 'pending',
        })

        // 最初のアクションを返す
        return response.results[0] ?? null
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status

        // 202: 処理中、4xx/5xx: エラー → nullを返してポーリング継続/停止判定
        if (status === 202 || (status && status >= 400)) {
          return null
        }
        throw error
      }
    },
    enabled: !!yakurekiId,
    refetchInterval: (query) => {
      // データ取得できたら停止
      if (query.state.data) {
        return false
      }

      // 最大ポーリング回数に達したら停止
      if (query.state.dataUpdateCount >= MAX_POLLS) {
        return false
      }

      // 固定間隔でポーリング続行
      return POLLING_INTERVAL
    },
    staleTime: 0, // ポーリング中は常に新鮮なデータを取得
  })

  return {
    homeCareAction: query.data ?? null,
    isLoading: query.isLoading || query.isFetching,
  }
}

// ========================================
// usePostSoapProcessingQuery (統合フック)
// ========================================

interface UsePostSoapProcessingQueryReturn {
  homeCareAction: Action | null
  loading: {
    homeCareAction: boolean
  }
}

/**
 * SOAP生成後処理統合クエリ（TanStack Query）
 *
 * 責務:
 * - 在宅介入判定の取得
 * - 固定間隔ポーリング（2秒、最大5回）で単純化
 *
 * 使用例:
 * ```tsx
 * const { homeCareAction, loading } =
 *   usePostSoapProcessingQuery('request-event-id')
 * ```
 */
export const usePostSoapProcessingQuery = (
  yakurekiId: string | undefined
): UsePostSoapProcessingQueryReturn => {
  const { homeCareAction, isLoading: actionLoading } =
    useHomeCareActionQuery(yakurekiId)

  return {
    homeCareAction,
    loading: {
      homeCareAction: actionLoading,
    },
  }
}
